import { Box } from '@mui/material'
import { GridActionsCellItem, GridColumns, GridNativeColTypes, GridRowParams } from '@mui/x-data-grid-pro'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MdDelete, MdEdit } from 'react-icons/md'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import GenericTable from 'src/components/table/GenericTable'
import { IStorage } from 'src/generated-types/storage-types'
import NoEquipment from '../../assets/svg/no-items-common-image.svg'

interface StorageTableProps {
  data: Array<IStorage> | undefined
  isLoading: boolean
  handleRemove?: (id: EntityId) => void
  handleEdit?: (initialValues: IStorage) => void
  onDetailsPanel?: ((params: GridRowParams<any>) => React.ReactNode) | undefined
}

const StorageTable = ({ data, isLoading, handleRemove, handleEdit, onDetailsPanel }: StorageTableProps) => {
  const { t } = useTranslation('storage')
  const STATIC_COLUMNS: GridColumns = [
    {
      field: 'name',
      headerName: t('name', { ns: 'common' }),
      flex: 2,
      renderCell: params => <DynamicTextTypography variant="body1" text={params.row.name} />,
    },
    {
      field: 'address',
      headerName: t('address', { ns: 'common' }),
      flex: 2,
      renderCell: params => <DynamicTextTypography variant="body1" text={params.row.address} />,
    },
  ]

  const renderActions = useCallback(
    ({ row, id }) => {
      const actions: JSX.Element[] = []
      if (handleEdit) {
        actions.push(
          <GridActionsCellItem
            key={id}
            label={t('edit', { ns: 'common' })}
            icon={<MdEdit size={24} />}
            onClick={() => {
              handleEdit(row)
            }}
            color="primary"
            showInMenu
          />,
        )
      }
      if (handleRemove) {
        actions.push(
          <GridActionsCellItem
            key={id}
            label={t('delete', { ns: 'common' })}
            icon={<MdDelete size={24} />}
            onClick={() => {
              handleRemove(row.id)
            }}
            color="primary"
            showInMenu
          />,
        )
      }
      return actions
    },
    [t, handleRemove, handleEdit],
  )

  const columns: GridColumns = useMemo(
    () => [
      ...STATIC_COLUMNS,
      ...[
        {
          field: 'actions',
          type: 'actions' as GridNativeColTypes,
          headerName: '',
          width: 60,
          getActions: renderActions,
        },
      ],
    ],
    [t, handleRemove, handleEdit],
  )

  return (
    <GenericTable
      id={'admin_storage_table'}
      columns={columns}
      getRowHeight={() => 'auto'}
      data={data}
      loading={isLoading}
      getDetailPanelHeight={() => 'auto'}
      getDetailPanelContent={onDetailsPanel ? params => onDetailsPanel(params) : undefined}
      noDataComponent={() =>
        data?.length == 0 ? (
          <Box height="100%">
            <EmptyStatusCard
              imageWidth="165px"
              image={NoEquipment}
              header={t('storage-empty-status-title', { ns: 'storage' })}
              content={t('storage-empty-status-content', { ns: 'storage' })}
              headerFontSize="18px"
              contentFontSize="14px"
              contentClasses="mt-2"
              contentTopMargin="10px"
              imageContainerClasses="pb-4 pt-4"
              height="100%"
            />
          </Box>
        ) : null
      }
    />
  )
}

export default StorageTable
